import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useFieldArray, useFormContext } from 'react-hook-form';
import SvgChevron from '@app/shared/icons/Chevron';
import styles from '../../../opm-inputs.module.scss';
import useLocalStorageCollapseState from '@app/core/hooks/useLocalStoreCollapse';
import { OpmInputFormModel2 } from '@app/modules/projects/inputs/opm-inputs-2/OpmInputs';
import { useLocale } from '@app/core/hooks/useLocale';
import { SummaryShareholderDebt } from './components/SummaryShareholderDebt';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { Pill, PillSize, PillTheme } from '@app/shared/components/pill/Pill';
import { OpmInputsSlnAndPrefShareSectionERF } from '../sln-and-pref-share-2/OpmInputsSlnAndPrefShareSectionERF';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../../utils/OpmInputHelper';

export const ShareholderDebtSection = () => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project.projectDraft);
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );
  const historicColumnCount = Object.keys(
    filterHistoricEventDatesFromFirstEvent(opmInputResults?.historicEventDates ?? [])
  ).length;
  const { control } = useFormContext<OpmInputFormModel2>();
  const { fields } = useFieldArray({
    name: 'slnAndPrefSharesInstruments',
    control,
  });

  const [expanded, setExpanded] = useLocalStorageCollapseState(
    'expanded-shareholder-debt',
    project.id
  );

  if (!opmInputResults || !opmInputResults.instrumentInformation) {
    return <></>;
  }

  const toggleCollapse = () => {
    setExpanded(!expanded);
  };

  const maxColspanCount =
    project.opmInput.forecastYears !== null
      ? getMaxColumnCount(
          historicColumnCount,
          project.opmInput.forecastYears,
          showHistoricDataColumns
        )
      : 0;
  const titleRowContentColSpan = maxColspanCount - 1;

  const showShareholderDebtSection = fields.length > 0;

  if (!showShareholderDebtSection) {
    return <></>;
  }

  return (
    <>
      <tbody>
        <tr data-testid="sd-title-section">
          <th className={`table-primary__cell--header-tertiary ${styles[`opm-header`]}`}>
            <div className={styles['opm-collapsable-header']}>
              <button
                type="button"
                aria-label={l('_ToggleSectionCollapse', {
                  section: l('_ShareholderDebtTitle'),
                })}
                aria-expanded={expanded}
                style={{ border: 'none', backgroundColor: 'none', background: 'none' }}
                onClick={toggleCollapse}
                data-testid="sd-collapse-button">
                {!expanded ? (
                  <SvgChevron
                    onClick={toggleCollapse}
                    className={styles[`opm-chevron-collapsed`]}
                  />
                ) : (
                  <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-expanded`]} />
                )}
              </button>
              {l('_ShareholderDebtTitle')}
            </div>
          </th>
          <th
            className={`table-primary__cell--header-tertiary ${styles[`opm-header-badge`]}`}
            colSpan={titleRowContentColSpan}>
            {project.details.calcMethod !== CalcMethod.OPM && (
              <div className={styles['opm-collapsable-header-badge']} data-testid="sd-badge">
                <Pill size={PillSize.Xsmall} noTextTransform theme={PillTheme.White}>
                  {l(`_Synced`)}
                </Pill>
              </div>
            )}
          </th>
        </tr>
        {expanded && (
          <>
            <SummaryShareholderDebt />
            <OpmInputsSlnAndPrefShareSectionERF />
          </>
        )}
      </tbody>
    </>
  );
};

export default ShareholderDebtSection;
